/* eslint-disable @eslint-react/no-array-index-key */
import { ProductOffering, Token } from '@travelwin/core';
import React, { Fragment, JSX, ReactNode } from 'react';

import { generateHeadingInfo } from '../../../app/helpers/generateHeadingId';
import {
  isLexicalBlockNode,
  traverseLexicalNodes,
} from '../../utils/lexicalUtilts';
import { GoogleFormEmbed } from '../GoogleFormEmbed/GoogleFormEmbed';
import { GoogleMapEmbed } from '../GoogleMapEmbed';
import { ImageFigure } from '../ImageFigure';
import { ImageMedia } from '../ImageMedia';
import { PostPlanCard } from '../PostPlanCard';
import { PostPlanPrice } from '../PostPlanPrice';
import { RichTextLink } from '../RichTextLink';
import { SocialEmbed } from '../SocialEmbed';
import { RedditCommentEmbed, RedditPostEmbed } from '../RedditEmbed';
import HighlightBox from '../HighlightBox';

// improve after the upgrade to payload v3
export type NodeTypes = any;

// source: https://github.com/facebook/lexical/blob/main/packages/lexical/src/LexicalConstants.ts
const IS_BOLD = 1;
const IS_ITALIC = 1 << 1;
const IS_STRIKETHROUGH = 1 << 2;
const IS_UNDERLINE = 1 << 3;
const IS_CODE = 1 << 4;
const IS_SUBSCRIPT = 1 << 5;
const IS_SUPERSCRIPT = 1 << 6;

const getAlignClassName = (format?: string) => {
  if (format && ['center', 'right', 'justify'].includes(format)) {
    return `text-${format}`;
  }
};

export type BlocksData = {
  plans?: ProductOffering[];
};

type RichTextContent = {
  root: {
    children: NodeTypes[];
  };
};

const isRichText = (content: unknown): content is RichTextContent => {
  return (
    typeof content === 'object' &&
    content !== null &&
    'root' in content &&
    typeof content.root === 'object' &&
    content.root !== null &&
    'children' in content.root &&
    Array.isArray(content.root.children)
  );
};

export function serializeLexical({
  content,
  data,
}: {
  content: unknown;
  data?: BlocksData;
}) {
  if (!isRichText(content)) {
    return null;
  }
  return serializeLexicalNodes({ nodes: content.root.children, data });
}

/**
 * Can be used to render simple rich text elements (bold, italic, underline, links etc.)
 * into elements that accept phrasing content (e.g. headings, figcaptions).
 */
export function serializeLexicalChildNode({
  content,
  data,
}: {
  content: unknown;
  data?: BlocksData;
}) {
  if (!isRichText(content)) {
    return null;
  }

  return serializeLexicalNodes({
    nodes: content.root.children.at(0)?.children,
    data,
  });
}

function serializeLexicalNodes({
  nodes,
  data,
}: {
  nodes: NodeTypes[];
  data?: BlocksData;
}): JSX.Element {
  return (
    <Fragment>
      {nodes?.map((node, index): JSX.Element | null => {
        if (node === null || node === undefined) {
          return null;
        }

        if (node.type === 'text') {
          let text = <React.Fragment key={index}>{node.text}</React.Fragment>;
          if (node.format & IS_BOLD) {
            text = <strong key={index}>{text}</strong>;
          }
          if (node.format & IS_ITALIC) {
            text = <em key={index}>{text}</em>;
          }
          if (node.format & IS_STRIKETHROUGH) {
            text = (
              <span key={index} style={{ textDecoration: 'line-through' }}>
                {text}
              </span>
            );
          }
          if (node.format & IS_UNDERLINE) {
            text = (
              <span key={index} style={{ textDecoration: 'underline' }}>
                {text}
              </span>
            );
          }
          if (node.format & IS_CODE) {
            text = <code key={index}>{node.text}</code>;
          }
          if (node.format & IS_SUBSCRIPT) {
            text = <sub key={index}>{text}</sub>;
          }
          if (node.format & IS_SUPERSCRIPT) {
            text = <sup key={index}>{text}</sup>;
          }

          return text;
        }

        const serializedChildrenFn = (node: NodeTypes): JSX.Element | null => {
          if (node.children === null || node.children === undefined) {
            return null;
          } else {
            return serializeLexicalNodes({
              nodes: node.children as NodeTypes[],
              data,
            });
          }
        };

        const serializedChildren =
          'children' in node ? serializedChildrenFn(node) : '';

        if (isLexicalBlockNode(node)) {
          const block = node.fields;

          switch (block.blockType) {
            case 'image':
              return <ImageFigure key={index} block={block} />;

            case 'social-embed':
              return (
                <SocialEmbed key={index} type={block.type} url={block.url} />
              );

            case 'google-form-embed':
              return (
                <GoogleFormEmbed
                  key={index}
                  src={block.src}
                  width={block.width ?? undefined}
                  height={block.height ?? undefined}
                  title={block.title}
                />
              );

            case 'google-map-embed':
              return (
                <GoogleMapEmbed
                  key={index}
                  src={block.src}
                  width={block.width ?? undefined}
                  height={block.height ?? undefined}
                  title={block.title}
                />
              );

            case 'plan-card': {
              const plan = data?.plans?.find(({ id }) => id === block.planId);

              return plan ? <PostPlanCard key={index} plan={plan} /> : null;
            }

            case 'plan-price': {
              const plan = data?.plans?.find(({ id }) => id === block.planId);

              return plan ? <PostPlanPrice key={index} plan={plan} /> : null;
            }

            case 'token': {
              const { text, color } = block;

              return (
                <Token key={index} color={color}>
                  {text}
                </Token>
              );
            }

            case 'reddit-post-embed':
              return (
                <RedditPostEmbed
                  key={index}
                  postUrl={block.postUrl}
                  author={block.author}
                  subreddit={block.subreddit}
                  height={block.height}
                  theme={block.theme}
                  created={block.created}
                  hideEdits={block.hideEdits}
                  hideUsername={block.hideUsername}
                  hideMedia={block.hideMedia}
                  locale={block.locale}
                />
              );

            case 'reddit-comment-embed':
              return (
                <RedditCommentEmbed
                  key={index}
                  commentUrl={block.commentUrl}
                  discussionUrl={block.discussionUrl}
                  author={block.author}
                  subreddit={block.subreddit}
                  height={block.height}
                  theme={block.theme}
                  context={block.context}
                  depth={block.depth}
                  created={block.created}
                  showTitle={block.showTitle}
                  hideEdits={block.hideEdits}
                  hideUsername={block.hideUsername}
                  locale={block.locale}
                />
              );

            case 'highlight-box-block': {
              const serializedText: ReactNode[] = [];

              traverseLexicalNodes(block.text.root, (innerNode) => {
                serializedText.push(
                  serializeLexicalNodes({
                    nodes: innerNode.children as NodeTypes[],
                  }),
                );
              });

              return (
                <HighlightBox
                  key={index}
                  backgroundColor={block.backgroundColor}
                  borderColor={block.borderColor}
                >
                  {serializedText}
                </HighlightBox>
              );
            }

            default:
              return null;
          }
        } else {
          switch (node.type) {
            case 'linebreak': {
              return <br key={index} />;
            }
            case 'paragraph': {
              return (
                <p key={index} className={getAlignClassName(node.format)}>
                  {serializedChildren}
                </p>
              );
            }
            case 'heading': {
              const Tag = node.tag;
              const headingInfo = generateHeadingInfo(node);
              const id = headingInfo ? headingInfo.id : undefined;
              return (
                <Tag
                  key={index}
                  id={id}
                  className={getAlignClassName(node.format)}
                >
                  {serializedChildren}
                </Tag>
              );
            }
            case 'list': {
              const Tag = node?.tag;
              return <Tag key={index}>{serializedChildren}</Tag>;
            }
            case 'listitem': {
              return (
                <li
                  key={index}
                  value={node?.value}
                  className={getAlignClassName(node.format)}
                >
                  {serializedChildren}
                </li>
              );
            }
            case 'quote': {
              return (
                <blockquote
                  key={index}
                  className={getAlignClassName(node.format)}
                >
                  {serializedChildren}
                </blockquote>
              );
            }
            case 'link': {
              const { fields } = node;

              return (
                <RichTextLink
                  key={index}
                  url={fields.url}
                  newTab={fields.newTab}
                >
                  {serializedChildren}
                </RichTextLink>
              );
            }
            case 'upload': {
              const { value } = node;

              return <ImageMedia key={index} media={value} loading="lazy" />;
            }

            default:
              return null;
          }
        }
      })}
    </Fragment>
  );
}
